/** Sticks expanded issue headers below navbar + device switcher while scrolling. */
import { useState, useEffect } from "react";

export const stickyIssueHeaderClass = "sticky-issue-header";

export const stickyIssueHeaderWrapClass = "sticky-issue-header-wrap";

/** Shared corner radius — always use var(--radius) via these classes (see index.css). */
export const issueCardOpenShellClass =
  "issue-card-shell issue-card-open border border-border bg-card shadow-sm";

/** Open issue: clip scrolling body content to the card shape. */
export const issueCardBodyClass =
  "issue-card-body overflow-hidden px-4 pb-4 pt-0";

/** Collapsed issue card. */
export const issueCardCollapsedClass =
  "issue-card-shell issue-card-collapsed border border-border bg-card shadow-sm";

const DEFAULT_STICKY_TOP = "68px";

export function setIssueStickyTop(px) {
  if (typeof document === "undefined") return;
  document.documentElement.style.setProperty(
    "--issue-sticky-top",
    `${Math.round(px)}px`,
  );
}

export function resetIssueStickyTop() {
  if (typeof document === "undefined") return;
  document.documentElement.style.setProperty(
    "--issue-sticky-top",
    DEFAULT_STICKY_TOP,
  );
}

export function syncIssueStickyTop(
  navElement,
  switcherSlotEl,
  isSwitcherFixed,
) {
  if (!navElement) {
    resetIssueStickyTop();
    return;
  }

  const navGap = 4;
  const navHeight = navElement.offsetHeight;

  if (isSwitcherFixed && switcherSlotEl) {
    const bar = switcherSlotEl.querySelector("[data-switcher-bar]");
    if (bar) {
      const { bottom } = bar.getBoundingClientRect();
      setIssueStickyTop(bottom);
      return;
    }
  }

  setIssueStickyTop(navHeight + navGap);
}

function readStickyTopPx() {
  if (typeof document === "undefined") return 68;
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue("--issue-sticky-top")
    .trim();
  return parseInt(raw, 10) || 68;
}

export function useStickyIssueStuck(headerRef, active) {
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    const header = headerRef.current;
    if (!active || !header) {
      setIsStuck(false);
      return;
    }

    let ticking = false;
    const check = () => {
      const stickyTop = readStickyTopPx();
      const { top } = header.getBoundingClientRect();
      setIsStuck(top <= stickyTop + 1);
    };

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          check();
          ticking = false;
        });
        ticking = true;
      }
    };

    check();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [active, headerRef]);

  return isStuck;
}
